import { useMemo, useState } from 'react';

import { fmt } from '@dcc-erp/shared';
import type { PettyCashFloatListRow, PettyCashVatTreatment } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { round2, todayIso, type Branch } from './pc-helpers';
import { useCoa } from '../../lib/queries/coa';
import { useCreatePettyCashEntry } from '../../lib/queries/petty-cash';
import { useVendorByTaxId } from '../../lib/queries/vendors';
import { DepartmentSelect } from '../dimensions';
import { ReceiptDropzone } from '../petty-cash/ReceiptDropzone';
import { SearchSelect } from '../SearchSelect';

interface PCEntryModalProps {
  branch: Branch | null;
  companyId: string | undefined;
  liveFloats: readonly PettyCashFloatListRow[];
  onClose: () => void;
}

export function PCEntryModal({ branch, companyId, liveFloats, onClose }: PCEntryModalProps) {
  const { data: coa = [] } = useCoa(companyId, true);
  const expenseAccounts = useMemo(
    () => coa.filter((a) => (a.type === 'EXPENSE' || a.type === 'COST') && !a.isControl),
    [coa],
  );
  const [categoryCode, setCategoryCode] = useState<string>('');
  // Live float picker: defaults to the first ACTIVE float, falls back to none.
  // We don't try to match selBranch (mock) to a live float — branches table
  // is still prototype mock data without UUIDs.
  const activeFloats = useMemo(() => liveFloats.filter((f) => f.status === 'ACTIVE'), [liveFloats]);
  const [floatId, setFloatId] = useState<string>(activeFloats[0]?.id ?? '');
  const [entryDate, setEntryDate] = useState<string>(todayIso());
  const [description, setDescription] = useState<string>('');
  const [amountText, setAmountText] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');
  // M2 — document fields. The server hard-blocks an entry unless it carries a
  // staged attachmentId OR isNoDoc=true; the client mirrors that gate below.
  const [payeeName, setPayeeName] = useState<string>('');
  const [vendorTaxId, setVendorTaxId] = useState<string>('');
  const [receiptNo, setReceiptNo] = useState<string>('');
  const [attachmentId, setAttachmentId] = useState<string>('');
  const [stagedName, setStagedName] = useState<string | null>(null);
  const [isNoDoc, setIsNoDoc] = useState<boolean>(false);
  // M4 — optional input-VAT + WHT. amount stays GROSS (VAT-inclusive). All
  // optional: a plain entry with treatment '' submits exactly as before.
  const [vatTreatment, setVatTreatment] = useState<'' | PettyCashVatTreatment>('');
  const [vatRateText, setVatRateText] = useState<string>('7');
  const [vatAmountText, setVatAmountText] = useState<string>('');
  const [taxInvoiceNo, setTaxInvoiceNo] = useState<string>('');
  const [taxInvoiceDate, setTaxInvoiceDate] = useState<string>('');
  const [whtIncomeType, setWhtIncomeType] = useState<string>('');
  const [whtRateText, setWhtRateText] = useState<string>('');
  const [whtAmountText, setWhtAmountText] = useState<string>('');
  const { mutate: createEntry, isPending, error } = useCreatePettyCashEntry(companyId);

  // Vendor auto-lookup by 13-digit tax ID. We DON'T setState in an effect
  // (forbidden by react-hooks/set-state-in-effect); instead we surface a hint +
  // an explicit "ใช้ชื่อนี้" button that fills payeeName on click.
  const vendorQ = useVendorByTaxId(companyId, vendorTaxId.length === 13 ? vendorTaxId : undefined);
  const vendorMatch = vendorQ.data;

  // Client mirror of the server receipt hard-block.
  const hasDocOrNoDoc = !!attachmentId || isNoDoc;

  const selectedFloat = activeFloats.find((f) => f.id === floatId);

  // M4 — derived tax breakdown. amount is GROSS (VAT-incl): expense booked NET
  // of VAT, cash leaving the tin is amount − WHT.
  const grossAmount = Number(amountText) || 0;
  const vatAmount = vatTreatment ? Number(vatAmountText) || 0 : 0;
  const whtAmount = Number(whtAmountText) || 0;
  const expenseNet = grossAmount - vatAmount;
  const cashOut = grossAmount - whtAmount;

  // FIX 6 — remain-after must net the WHT: the float decrements by cashOut
  // (gross − WHT), NOT the gross. Subtracting the gross would understate the
  // projected balance by the withheld amount and diverge from the server-side
  // float decrement + the cash-book running balance.
  const remainAfter = selectedFloat ? Number(selectedFloat.currentBalance) - cashOut : null;

  // FIX 5-web — the WHT amount is DERIVED from the rate, mirroring the server's
  // authoritative recompute (whtAmount = round2((gross − vatAmount) × rate/100)).
  // The server now validates a supplied whtAmount against this rate, so the web
  // must send a rate-consistent figure. The field stays editable (the user can
  // type an override), but changing the rate / gross / VAT amount recomputes it.
  function deriveWhtFromRate(rateText: string, gross: number, vat: number): string {
    const rate = Number(rateText);
    if (!rateText.trim() || !Number.isFinite(rate) || rate <= 0) return whtAmountText;
    const base = gross - vat;
    if (base <= 0) return '0';
    return String(round2((base * rate) / 100));
  }

  function handleSubmit() {
    if (!floatId || !description.trim() || !amountText || !categoryCode) return;
    // Receipt gate: must have a staged attachment OR the explicit no-doc flag.
    if (!hasDocOrNoDoc) return;
    const amount = Number(amountText);
    if (!Number.isFinite(amount) || amount <= 0) return;
    // M4 — VAT block: only when a treatment is chosen. WHT block: only when an
    // amount is withheld. Everything else round-trips exactly as before.
    const vatFields = vatTreatment
      ? {
          vatTreatment,
          ...(vatRateText.trim() ? { vatRate: Number(vatRateText) } : {}),
          ...(vatAmountText.trim() ? { vatAmount: Number(vatAmountText) } : {}),
          ...(taxInvoiceNo.trim() ? { taxInvoiceNo: taxInvoiceNo.trim() } : {}),
          ...(taxInvoiceDate ? { taxInvoiceDate } : {}),
        }
      : {};
    const whtFields =
      whtAmountText.trim() && Number(whtAmountText) > 0
        ? {
            whtAmount: Number(whtAmountText),
            ...(whtRateText.trim() ? { whtRate: Number(whtRateText) } : {}),
            ...(whtIncomeType.trim() ? { whtIncomeType: whtIncomeType.trim() } : {}),
          }
        : {};
    createEntry(
      {
        data: {
          floatId,
          entryDate,
          description: description.trim(),
          categoryCode,
          amount,
          ...(departmentId ? { departmentId } : {}),
          payeeName: payeeName.trim() ? payeeName.trim() : undefined,
          vendorTaxId: vendorTaxId.trim() ? vendorTaxId.trim() : undefined,
          receiptNo: receiptNo.trim() ? receiptNo.trim() : undefined,
          attachmentId: attachmentId ? attachmentId : undefined,
          isNoDoc,
          ...vatFields,
          ...whtFields,
        },
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 700,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              บันทึกรายการเงินสดย่อย
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              {branch ? (
                <>
                  สาขา:{' '}
                  <b style={{ color: '#3f3f46' }}>
                    {branch.id} — {branch.name}
                  </b>{' '}
                  · เงินคงเหลือ {fmt(branch.remain)} บาท
                </>
              ) : (
                <>เลือก float ของสาขาจากช่องด้านล่าง</>
              )}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div
          style={{
            padding: 22,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            overflow: 'auto',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                วันที่ทำรายการ
              </label>
              <input
                className="input"
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                สาขา (Float)
              </label>
              <select
                className="select"
                value={floatId}
                onChange={(e) => setFloatId(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
                disabled={activeFloats.length === 0}
              >
                {activeFloats.length === 0 && <option value="">— ไม่มี float ที่ active —</option>}
                {activeFloats.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.branchName} · คงเหลือ {fmt(Number(f.currentBalance))} บาท
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              ประเภทค่าใช้จ่าย (ผังบัญชี)
            </label>
            <SearchSelect
              value={categoryCode}
              onChange={setCategoryCode}
              placeholder="— เลือกบัญชีค่าใช้จ่าย —"
              options={expenseAccounts.map((a) => ({
                value: a.code,
                label: `${a.code} — ${a.nameTh}`,
              }))}
              getLabel={(v) => {
                const a = expenseAccounts.find((x) => x.code === v);
                return a ? `${a.code} — ${a.nameTh}` : '';
              }}
            />
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              รายละเอียด
            </label>
            <input
              className="input"
              placeholder="เช่น เติมน้ำมัน 95 รถ Hilux ทะเบียน ขข-1234"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              หน่วยงาน (Cost Center)
            </label>
            <DepartmentSelect
              companyId={companyId}
              value={departmentId}
              onChange={setDepartmentId}
              branchId={selectedFloat?.branchId ?? ''}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ผู้รับเงิน / ร้านค้า
              </label>
              <input
                className="input"
                placeholder="เช่น ปั๊ม ปตท. สาขาบางนา"
                value={payeeName}
                onChange={(e) => setPayeeName(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                จำนวนเงิน (บาท)
              </label>
              <input
                className="input tnum"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={amountText}
                onChange={(e) => {
                  const next = e.target.value;
                  setAmountText(next);
                  // FIX 5-web — keep the rate-derived WHT amount in sync when the
                  // gross changes (base = gross − VAT). No-op when no WHT rate.
                  setWhtAmountText(deriveWhtFromRate(whtRateText, Number(next) || 0, vatAmount));
                }}
                style={{
                  width: '100%',
                  height: 38,
                  fontSize: 16,
                  textAlign: 'right',
                  fontWeight: 600,
                }}
              />
            </div>
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              เลขประจำตัวผู้เสียภาษี (13 หลัก)
            </label>
            <input
              className="input"
              placeholder="ระบุเพื่อค้นหาผู้ขายอัตโนมัติ (ไม่บังคับ)"
              value={vendorTaxId}
              onChange={(e) => setVendorTaxId(e.target.value.replace(/\D/g, '').slice(0, 13))}
              inputMode="numeric"
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
            {vendorMatch && (
              <div
                style={{
                  marginTop: 6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                }}
              >
                <span className="muted">
                  ตรงกับผู้ขาย: <b style={{ color: '#3f3f46' }}>{vendorMatch.nameTh}</b>
                </span>
                {payeeName.trim() !== vendorMatch.nameTh && (
                  <button
                    type="button"
                    className="btn sm ghost"
                    onClick={() => setPayeeName(vendorMatch.nameTh)}
                  >
                    ใช้ชื่อนี้
                  </button>
                )}
              </div>
            )}
            {vendorTaxId.length === 13 && vendorQ.isError && (
              <div className="meta" style={{ marginTop: 6 }}>
                ไม่พบผู้ขายที่ตรงกับเลขภาษีนี้ — กรอกชื่อผู้รับเงินเอง
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                เลขที่ใบเสร็จ / ใบกำกับ
              </label>
              <input
                className="input"
                placeholder="เช่น INV-2026-00123"
                value={receiptNo}
                onChange={(e) => setReceiptNo(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ภาษีซื้อ (Input VAT)
              </label>
              <select
                className="select"
                data-testid="pc-entry-vat-treatment"
                value={vatTreatment}
                onChange={(e) => setVatTreatment(e.target.value as '' | PettyCashVatTreatment)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              >
                <option value="">ไม่มีภาษีซื้อ</option>
                <option value="CLAIMABLE">ภาษีซื้อขอคืนได้ (Claimable)</option>
                <option value="SUSPENSE">ภาษีซื้อค้างรับ (Suspense)</option>
                <option value="PROHIBITED">ภาษีซื้อต้องห้าม (Prohibited)</option>
              </select>
            </div>
          </div>

          {/* VAT detail — only when a treatment is selected. amount stays GROSS. */}
          {vatTreatment && (
            <div
              data-testid="pc-entry-vat-section"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1fr',
                gap: 14,
                padding: '12px 14px',
                background: '#fafafa',
                borderRadius: 8,
                border: '1px solid var(--border)',
              }}
            >
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    fontWeight: 500,
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  อัตรา VAT (%)
                </label>
                <input
                  className="input tnum"
                  type="number"
                  step="0.01"
                  min="0"
                  value={vatRateText}
                  onChange={(e) => setVatRateText(e.target.value)}
                  style={{ width: '100%', height: 36, fontSize: 14, textAlign: 'right' }}
                />
              </div>
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    fontWeight: 500,
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  ยอด VAT (บาท)
                </label>
                <input
                  className="input tnum"
                  data-testid="pc-entry-vat-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="ยอดภาษีซื้อ"
                  value={vatAmountText}
                  onChange={(e) => {
                    const next = e.target.value;
                    setVatAmountText(next);
                    // FIX 5-web — the WHT base is pre-VAT (gross − VAT), so a VAT
                    // change recomputes the rate-derived WHT. No-op when no rate.
                    setWhtAmountText(
                      deriveWhtFromRate(whtRateText, grossAmount, Number(next) || 0),
                    );
                  }}
                  style={{ width: '100%', height: 36, fontSize: 14, textAlign: 'right' }}
                />
              </div>
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    fontWeight: 500,
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  เลขที่ใบกำกับภาษี
                </label>
                <input
                  className="input"
                  placeholder="(ไม่บังคับ)"
                  value={taxInvoiceNo}
                  onChange={(e) => setTaxInvoiceNo(e.target.value)}
                  style={{ width: '100%', height: 36, fontSize: 14 }}
                />
              </div>
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    fontWeight: 500,
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  วันที่ใบกำกับภาษี
                </label>
                <input
                  className="input"
                  type="date"
                  value={taxInvoiceDate}
                  onChange={(e) => setTaxInvoiceDate(e.target.value)}
                  style={{ width: '100%', height: 36, fontSize: 14 }}
                />
              </div>
            </div>
          )}

          {/* WHT — optional withholding at the payout. */}
          <div
            data-testid="pc-entry-wht-section"
            style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14 }}
          >
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ภาษีหัก ณ ที่จ่าย — ประเภทเงินได้
              </label>
              <input
                className="input"
                placeholder="เช่น ค่าบริการ (ม.40(8)) — ไม่บังคับ"
                value={whtIncomeType}
                onChange={(e) => setWhtIncomeType(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                อัตรา WHT (%)
              </label>
              <input
                className="input tnum"
                data-testid="pc-entry-wht-rate"
                type="number"
                step="0.01"
                min="0"
                value={whtRateText}
                onChange={(e) => {
                  const next = e.target.value;
                  setWhtRateText(next);
                  // FIX 5-web — derive the WHT amount from the rate (server-consistent).
                  setWhtAmountText(deriveWhtFromRate(next, grossAmount, vatAmount));
                }}
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ยอดหัก WHT (บาท)
              </label>
              <input
                className="input tnum"
                data-testid="pc-entry-wht-amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="ยอดหัก ณ ที่จ่าย"
                value={whtAmountText}
                onChange={(e) => setWhtAmountText(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
          </div>

          {/* Tax breakdown cue — only meaningful once VAT/WHT carved out. */}
          {(vatTreatment || whtAmount > 0) && (
            <div
              data-testid="pc-entry-tax-breakdown"
              style={{
                padding: '10px 12px',
                background: '#fafafa',
                borderRadius: 8,
                border: '1px solid var(--border)',
                fontSize: 13,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="muted">ยอดรวม (GROSS รวม VAT)</span>
                <span className="tnum">{fmt(grossAmount)} บาท</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="muted">ค่าใช้จ่ายสุทธิ (ไม่รวม VAT)</span>
                <span className="tnum" data-testid="pc-entry-expense-net">
                  {fmt(expenseNet)} บาท
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="muted">เงินสดที่จ่ายจริง (หัก WHT)</span>
                <span className="tnum" data-testid="pc-entry-cash-out" style={{ fontWeight: 600 }}>
                  {fmt(cashOut)} บาท
                </span>
              </div>
            </div>
          )}

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              แนบใบเสร็จ (ภาพถ่ายหรือ PDF)
            </label>
            <ReceiptDropzone
              companyId={companyId}
              stagedName={stagedName}
              onStaged={(id, name) => {
                setAttachmentId(id);
                setStagedName(name || null);
              }}
            />
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 10,
                fontSize: 14,
                color: 'var(--text-muted)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={isNoDoc}
                onChange={(e) => setIsNoDoc(e.target.checked)}
              />
              ไม่แนบใบเสร็จ (มีเอกสารกระดาษ/ใบสำคัญรับเงินภายใน)
            </label>
          </div>

          <div
            style={{
              padding: '10px 12px',
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid var(--border)',
              fontSize: 14,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span className="muted">เงินคงเหลือหลังบันทึก (ประมาณ)</span>
            <span
              style={{
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
                color: remainAfter !== null && remainAfter < 0 ? 'var(--negative)' : undefined,
              }}
              className="tnum"
            >
              {remainAfter !== null ? fmt(Math.round(remainAfter)) : fmt(branch?.remain ?? 0)} บาท
            </span>
          </div>
          {error && (
            <div
              style={{
                padding: '10px 12px',
                background: 'var(--negative-bg)',
                borderRadius: 8,
                border: '1px solid var(--negative)',
                color: 'var(--negative)',
                fontSize: 14,
              }}
            >
              {error.message}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose} disabled={isPending}>
            ยกเลิก
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn"
              onClick={handleSubmit}
              disabled={
                isPending ||
                !floatId ||
                !description.trim() ||
                !amountText ||
                !categoryCode ||
                !hasDocOrNoDoc
              }
            >
              บันทึก & เพิ่มอีก
            </button>
            <button
              className="btn primary"
              onClick={handleSubmit}
              disabled={
                isPending ||
                !floatId ||
                !description.trim() ||
                !amountText ||
                !categoryCode ||
                !hasDocOrNoDoc
              }
            >
              <Icon name="check" size={14} /> {isPending ? 'กำลังบันทึก…' : 'บันทึกรายการ'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export type { PCEntryModalProps };
